import React from 'react';
import { Box, Table, Thead, Tbody, Tr, ResponsiveValue, Td } from '@chakra-ui/react';
import { CustomTableHeading } from '../atoms/CustomTableHeading';
import { CustomTableRow } from '../atoms/CustomTableRow';

export interface CustomTableProps {
  fields: string[]; // The field names to be displayed as headings
  data: object[];
  fieldsWithIcons?: string[];
  fieldsAsLinks?: string[];
  centerAlignFields?: string[];
  mt?: ResponsiveValue<string>;
  height?: ResponsiveValue<string>;
  rowBorderColor?: string;
  rowHoverBgColor?: string;
  tableRowTextColor?: string;
  tableHeadingTextColor?: string;
  primaryAccentColor?: string;
  secondaryAccentColor?: string;
  tertiaryAccentColor?: string;
  onTicketClick?: (issueId: number, status: 'Active' | 'resolved' | 'rejected') => void;
  emptyDataHelperText: string;
  isPreview?: boolean;
  ticketHref?: (issueId: number, status: 'Active' | 'resolved' | 'rejected') => string;
  getTicketData?: (issueId: number) => any;
}
const handleTicketClick = (
  item: Record<string, any>,
  onTicketClick?: (issueId: number, status: 'Active' | 'resolved' | 'rejected') => void,
) => {
  if (!onTicketClick) return;

  const ticketNumber = Object.entries(item).find(([key]) => key.toLowerCase() === 'ticket #')?.[1];
  const ticketStatus = Object.entries(item).find(([key]) => key.toLowerCase() === 'status')?.[1];
  if (
    ticketNumber !== undefined &&
    (ticketStatus === 'Active' || ticketStatus === 'resolved' || ticketStatus === 'rejected')
  ) {
    onTicketClick(ticketNumber, ticketStatus);
  }
};

const ticketData = (item: Record<string, any>, getTicketData?: (issueId: number) => any) => {
  if (!getTicketData) return undefined;

  const ticketNumber = Object.entries(item).find(([key]) => key.toLowerCase() === 'ticket #')?.[1];
  if (ticketNumber !== undefined) {
    return getTicketData(ticketNumber);
  }
  return undefined;
};

const getTicketHref = (
  item: Record<string, any>,
  ticketHref?: (issueId: number, status: 'Active' | 'resolved' | 'rejected') => string,
) => {
  if (!ticketHref) return undefined;

  const ticketNumber = Object.entries(item).find(([key]) => key.toLowerCase() === 'ticket #')?.[1];
  const ticketStatus = Object.entries(item).find(([key]) => key.toLowerCase() === 'status')?.[1];
  if (
    ticketNumber !== undefined &&
    (ticketStatus === 'Active' || ticketStatus === 'resolved' || ticketStatus === 'rejected')
  ) {
    return ticketHref(ticketNumber, ticketStatus);
  }
  return undefined;
};
export const CustomTable: React.FC<CustomTableProps> = ({
  fields,
  data,
  fieldsWithIcons,
  centerAlignFields,
  fieldsAsLinks,
  mt,
  height,
  rowBorderColor = 'gray',
  rowHoverBgColor = 'gray.200',
  tableRowTextColor = 'black',
  tableHeadingTextColor = 'black',
  primaryAccentColor,
  secondaryAccentColor,
  tertiaryAccentColor,
  onTicketClick,
  emptyDataHelperText,
  isPreview,
  ticketHref,
  getTicketData,
}) => {
  return (
    <Box
      overflowX="auto"
      maxHeight={height}
      mt={mt}
      overflowY="auto"
      border="1px solid "
      borderColor="#CFCFCF"
      sx={{
        '&::-webkit-scrollbar': {
          width: '6px',
          height: '6px',
        },
        '&::-webkit-scrollbar-thumb': {
          background: 'gray.300',
          borderRadius: '3px',
        },
        '&::-webkit-scrollbar-track': {
          background: 'transparent',
        },
      }}
    >
      <Table width="100%" fontSize={{ base: 'sm', md: 'md', lg: 'md' }}>
        <Thead>
          <Tr position="sticky" top={0} zIndex={999}>
            {fields.map(field => (
              <CustomTableHeading
                bgColor={primaryAccentColor}
                key={field}
                field={field}
                fieldsWithIcons={fieldsWithIcons}
                centerAlignFields={centerAlignFields}
                tableHeadingTextColor={tableHeadingTextColor}
              />
            ))}
            <CustomTableHeading field="" bgColor={primaryAccentColor} />
          </Tr>
        </Thead>
        <Tbody>
          {data.length > 0 ? (
            data.map(item => (
              <CustomTableRow
                key={JSON.stringify(item)}
                fields={fields}
                item={item}
                fieldsAsLinks={fieldsAsLinks}
                rowBorderColor={rowBorderColor}
                rowHoverBgColor={rowHoverBgColor}
                tableRowTextColor={tableRowTextColor}
                onTicketClick={() => handleTicketClick(item, onTicketClick)}
                secondaryAccentColor={secondaryAccentColor}
                tertiaryAccentColor={tertiaryAccentColor}
                showArrow
                isPreview={isPreview}
                ticketHref={getTicketHref(item, ticketHref)}
                ticketData={ticketData(item, getTicketData)}
              />
            ))
          ) : (
            <Tr>
              <Td colSpan={fields.length + 1} textAlign="center" padding="1rem">
                {emptyDataHelperText || 'Loading...'}
              </Td>
            </Tr>
          )}
        </Tbody>
      </Table>
    </Box>
  );
};
